import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";
import { handleError } from "../../utils/handleError";
import logger from "../../utils/logger.utils";

const initialState = {
	isLoading: false,
	error: null,
	message: null,
	AverageFiles: null,
	AverageUser: null,
	AllUser: null,
	pagination: { page: 1, limit: 10, totalPages: 1 },
	AverageExpiresTime: null,
	DashboardChart: null,
	singleUser: null,
	UserGrowthRate: null,
	TotalUploadsPerDay: null,
	NewUsersPerDay: null,
	ActiveFileSharers: null,
	VisitorEngagement: null,
	FolderUploadFrequency: null,
	errorsLogs: [],
	DownloadingFileStats:[],
	logsPagination: { page: 1, limit: 10, totalPages: 1, total: 0 },
	StorageStats: null,
	UploadsByHour: null,
	EngagementSummary: null,
	BlockedFiles: null,
};

const setPending = (state) => { state.isLoading = true; };
const setRejected = (state, action) => { state.isLoading = false; state.error = action.payload?.message; };

const dashboardSlice = createSlice({
	name: 'dashboard',
	initialState,
	reducers: {
		clearError: (state) => { state.error = null; },
		clearMessage: (state) => { state.message = null; },
		clearSingleUser: (state) => { state.singleUser = null; }
	},
	extraReducers: (builder) => {
		builder
		// NEW: summary
		.addCase(getDashboardSummary.pending, setPending)
		.addCase(getDashboardSummary.rejected, setRejected)
		.addCase(getDashboardSummary.fulfilled, (state, action) => {
			state.isLoading = false;
			const p = action.payload || {};
			// map existing
			state.AverageUser = p.averageUser;
			state.AverageFiles = p.averageFiles;
			state.AverageExpiresTime = p.averageExpiresTime;
			state.DashboardChart = p.dashboardChart;
			state.NewUsersPerDay = p.newUsersPerDay;
			state.TotalUploadsPerDay = p.totalUploadsPerDay;
			state.UserGrowthRate = p.userGrowthRate;
			state.ActiveFileSharers = p.activeFileSharers;
			state.FolderUploadFrequency = p.folderUploadFrequency;
			// new fields
			state.StorageStats = p.storageStats;
			state.UploadsByHour = p.uploadsByHour;
			state.EngagementSummary = p.engagementSummary;
			state.BlockedFiles = p.blockedFiles;
		})

		.addCase(getDashboardData.pending, setPending)
		.addCase(getDashboardData.rejected, setRejected)
		.addCase(getDashboardData.fulfilled, (state, action) => {
			state.isLoading = false;
			state.AverageExpiresTime = action.payload;
			state.message = action.payload?.message || null;
		})

		.addCase(getDashboardChart.pending, setPending)
		.addCase(getDashboardChart.rejected, setRejected)
		.addCase(getDashboardChart.fulfilled, (state, action) => {
			state.isLoading = false;
			state.DashboardChart = action.payload;
			state.message = action.payload?.message || null;
		})

		.addCase(getDashboardUser.pending, setPending)
		.addCase(getDashboardUser.fulfilled, (state, action) => {
			state.isLoading = false;
			state.AllUser = action.payload?.users;
			state.pagination = action.payload?.pagination || null;
			state.message = action.payload?.message || null;
		})
		.addCase(getDashboardUser.rejected, setRejected)

		.addCase(getAverageUser.pending, setPending)
		.addCase(getAverageUser.fulfilled, (state, action) => {
			state.isLoading = false;
			state.AverageUser = action.payload;
			state.message = action.payload?.message || null;
		})
		.addCase(getAverageUser.rejected, setRejected)

		.addCase(getAverageFiles.pending, setPending)
		.addCase(getAverageFiles.fulfilled, (state, action) => {
			state.isLoading = false;
			state.AverageFiles = action.payload;
			state.message = action.payload?.message || null;
		})
		.addCase(getAverageFiles.rejected, setRejected)

		.addCase(getSingleUser.pending, setPending)
		.addCase(getSingleUser.fulfilled, (state, action) => {
			state.isLoading = false;
			state.singleUser = action.payload?.user;
			state.message = action.payload?.message || null;
		})
		.addCase(getSingleUser.rejected, setRejected)
		.addCase(deleteUserById.pending, setPending)
		.addCase(deleteUserById.fulfilled, (state, action) => {
			state.isLoading = false;
			state.message = action.payload?.message || null;
			state.AllUser = state.AllUser?.filter(user => user._id !== action.payload?.userId);
		}).addCase(deleteUserById.rejected, setRejected)
		.addCase(banUserById.pending, setPending)
		.addCase(banUserById.fulfilled, (state, action) => {
			state.isLoading = false;
			state.message = action.payload?.message || null;
			state.AllUser = state.AllUser?.map(user =>
			user._id === action.payload?.userId ? { ...user, isBanned: action.payload?.isBanned } : user
			);
		}).addCase(banUserById.rejected, setRejected)
		.addCase(getNewUsersPerDay.pending, setPending)
		.addCase(getNewUsersPerDay.fulfilled, (state, action) => {
			state.isLoading = false;
			state.NewUsersPerDay = action.payload?.data;
			state.message = action.payload?.message || null;
		})
		.addCase(getTotalUploadsPerDay.pending, setPending)
		.addCase(getTotalUploadsPerDay.rejected, setRejected)
		.addCase(getTotalUploadsPerDay.fulfilled, (state, action) => {
			state.isLoading = false;
			state.TotalUploadsPerDay = action.payload?.data;
			state.message = action.payload?.message || null;
		})
		.addCase(getUserGrowthRate.pending, setPending)
		.addCase(getUserGrowthRate.fulfilled, (state, action) => {
			state.isLoading = false;
			state.UserGrowthRate = action.payload?.data;
			state.message = action.payload?.message || null;
		})
		.addCase(getActiveFileSharers.pending, setPending)
		.addCase(getActiveFileSharers.rejected, setRejected)
		.addCase(getActiveFileSharers.fulfilled, (state, action) => {
			state.isLoading = false;
			state.ActiveFileSharers = action.payload?.data;
			state.message = action.payload?.message || null;
		})
		.addCase(getVisitorEngagement.pending, setPending)
		.addCase(getVisitorEngagement.rejected, setRejected)
		.addCase(getVisitorEngagement.fulfilled, (state, action) => {
			state.isLoading = false;
			state.VisitorEngagement = action.payload?.data;
			state.message = action.payload?.message || null;
		})
		.addCase(getDownloadingFileStats.pending, setPending)
		.addCase(getDownloadingFileStats.rejected, setRejected)
		.addCase(getDownloadingFileStats.fulfilled, (state, action) => {
			state.isLoading = false;
			state.DownloadingFileStats = action.payload?.data;
			state.message = action.payload?.message || null;
		})
		.addCase(getFolderUploadFrequency.pending, setPending)
		.addCase(getFolderUploadFrequency.rejected, setRejected)
		.addCase(getFolderUploadFrequency.fulfilled, (state, action) => {
			state.isLoading = false;
			state.FolderUploadFrequency = action.payload?.data;
			state.message = action.payload?.message || null;
		})
		.addCase(getErrorsLog.pending, setPending)
		.addCase(getErrorsLog.rejected, setRejected)
		.addCase(getErrorsLog.fulfilled, (state, action) => {
			state.isLoading = false;
			state.errorsLogs = action.payload?.files;
			state.logsPagination = action.payload?.pagination || state.logsPagination;
			state.message = action.payload?.message || null;
		})

		.addCase(updateToken.pending, setPending)
		.addCase(updateToken.rejected, setRejected)
		.addCase(updateToken.fulfilled, (state, action) => {
			state.isLoading = false;
			state.message = action.payload?.message || null;
			const updated = action.payload?.user;
			if (updated?._id) {
				state.singleUser = updated;
				state.AllUser = state.AllUser?.map(u => u._id === updated._id ? { ...u, tokenBalance: updated.tokenBalance } : u);
			}
		})
		.addCase(updateTokensForUsers.pending, setPending)
		.addCase(updateTokensForUsers.rejected, setRejected)
		.addCase(updateTokensForUsers.fulfilled, (state, action) => {
			state.isLoading = false;
			state.message = action.payload?.message || null;

			const targetIds = Array.isArray(action.payload?.userIds) ? action.payload.userIds : [];
			const nextToken = Number(action.payload?.token);

			if (targetIds.length && Number.isFinite(nextToken)) {
				state.AllUser = state.AllUser?.map((u) =>
					targetIds.includes(u._id) ? { ...u, tokenBalance: nextToken } : u
				);

				if (state.singleUser?._id && targetIds.includes(state.singleUser._id)) {
					state.singleUser = { ...state.singleUser, tokenBalance: nextToken };
				}
			}
		})
		.addCase(updateUserById.pending, setPending)
		.addCase(updateUserById.rejected, setRejected)
		.addCase(updateUserById.fulfilled, (state, action) => {
			state.isLoading = false;
			state.message = action.payload?.message || null;
			const updated = action.payload?.user;
			if (updated?._id) {
				state.singleUser = updated;
				state.AllUser = state.AllUser?.map(u => u._id === updated._id ? { ...u, ...updated } : u);
			}
		})
	},
});

// NEW: summary thunk
export const getDashboardSummary = createAsyncThunk(
	'dashboard/getDashboardSummary',
	async ({ chartType = 'last7days', days = 7, sharersLimit = 10, avgExpireDays = 30 } = {}, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get(
				`/admin/dashboard/summary?chartType=${encodeURIComponent(chartType)}&days=${days}&sharersLimit=${sharersLimit}&avgExpireDays=${avgExpireDays}`
			);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

// Existing thunks remain...
export const getDashboardData = createAsyncThunk(
	'dashboard/getDashboardData',
	async (_, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get('/admin/dashboard/dashboard-average-expireTime');
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const getErrorsLog = createAsyncThunk(
	'dashboard/getErrorsLog',
	async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
	try {
		const res = await axiosInstance.get(`/admin/dashboard/logs?page=${page}&limit=${limit}`);
		return res.data;
	} catch (error) {
		return rejectWithValue(handleError(error));
	}
});

export const getDashboardChart = createAsyncThunk(
	'dashboard/getDashboardChart',
	async (_, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get('/admin/dashboard/dashboard-chart');
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const getDashboardUser = createAsyncThunk(
	'dashboard/getDashboardUser',
	async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
		try {

			const params = new URLSearchParams(window.location.search);

			params.set('page', page);
			params.set('limit', limit);
			const res = await axiosInstance.get(
				`/admin/dashboard/dashboard-user?${params.toString()}`
			);

			return res.data;

		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const getSingleUser = createAsyncThunk(
	'dashboard/getSingleUser',
	async (id, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get(`/admin/dashboard/fetch-user-id/${id}`);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const deleteUserById = createAsyncThunk(
	'dashboard/deleteUserById',
	async (id, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.delete(`/admin/dashboard/user/${id}`);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const banUserById = createAsyncThunk(
	'dashboard/banUserById',
	async (id, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.patch(`/admin/dashboard/user-ban/${id}`);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const trySendMail = createAsyncThunk(
	'dashboard/trySendMail',
	async (emailData, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.post('/admin/dashboard/send-email', emailData);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);
export const updateToken = createAsyncThunk(
	'dashboard/updateToken',
	async ({ userId, newBalance }, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.patch(`/admin/dashboard/update-token/${userId}`, { newBalance });
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const updateTokensForUsers = createAsyncThunk(
	'dashboard/updateTokensForUsers',
	async ({ userIds, token }, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.post('/admin/dashboard/update-token-user', {
				userIds,
				token,
			});

			return {
				...res.data,
				userIds,
				token,
			};
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const updateUserById = createAsyncThunk(
	'dashboard/updateUserById',
	async ({ id, updates }, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.patch(`/admin/dashboard/user-update/${id}`, updates);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const getAverageUser = createAsyncThunk(
  'dashboard/getAverageUser',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/admin/dashboard/average-user');
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const getAverageFiles = createAsyncThunk(
  'dashboard/getAverageFiles',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/admin/dashboard/average-file');
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const getNewUsersPerDay = createAsyncThunk(
  'dashboard/getNewUsersPerDay',
  async ({days = 7}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/admin/dashboard/new-user?days=${days}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const getTotalUploadsPerDay = createAsyncThunk(
  'dashboard/getTotalUploadsPerDay',
  async ({days = 7}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/admin/dashboard/total-uploads-perDay?days=${days}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
)
export const getUserGrowthRate = createAsyncThunk(
  'dashboard/getUserGrowthRate',
  async ({days = 7}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/admin/dashboard/user-growth-rate?days=${days}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const getActiveFileSharers = createAsyncThunk(
  'dashboard/getActiveFileSharers',
  async ({days = 7}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/admin/dashboard/most-active-file-sharers?days=${days}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);
export const getDownloadingFileStats = createAsyncThunk(
	'dashboard/getDownloadingFileStats',
	async ({days = 7}, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get(`/admin/dashboard/downloading-file-stats?days=${days}`);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	})

export const getVisitorEngagement = createAsyncThunk(
  'dashboard/getEngagement',
  async({days = 7},{rejectWithValue}) => {
    try {
      const res = await axiosInstance.get(`/admin/dashboard/visitor-engagement?days=${days}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const getFolderUploadFrequency = createAsyncThunk(
  'dashboard/getFolderUploadFrequency',
  async ({days = 7}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/admin/dashboard/folders-freq?days=${days}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const { clearError, clearMessage, clearSingleUser } = dashboardSlice.actions;
export default dashboardSlice.reducer;
